import { T } from 'gt-react';
import type { TaskTypeViewLineListPlacement } from '@erp/shared-types';
import { Label } from 'src/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { TaskTypeViewField } from './TaskTypeViewField';
import { TaskTypeViewMatch } from './TaskTypeViewMatch';
import type { CorrectionCommitResult } from './useCommitTaskCorrection';
import {
  findSchemaField,
  getValueAtPath,
  placementLabel,
  readRowArray,
  replaceRow,
  rowObject,
  setValueAtPath,
  type TaskViewSchemaField,
} from './taskTypeViewValue';

type Commit = (
  next: Record<string, unknown>,
) => void | Promise<CorrectionCommitResult | void>;

export function TaskTypeViewLineList({
  placement,
  schemaFields,
  input,
  workingCopy,
  onCommit,
}: {
  placement: TaskTypeViewLineListPlacement;
  schemaFields: readonly TaskViewSchemaField[];
  input: Record<string, unknown>;
  workingCopy: Record<string, unknown>;
  onCommit: Commit;
}) {
  const { config } = placement;
  const arrayField = findSchemaField(schemaFields, config.path);
  const rowFields = arrayField?.children ?? [];
  const rows = readRowArray(getValueAtPath(workingCopy, config.path));
  const inputRows = readRowArray(getValueAtPath(input, config.path));
  const label = config.label?.trim() ? config.label : null;

  function commitRow(index: number, nextRow: Record<string, unknown>) {
    return onCommit(
      setValueAtPath(
        workingCopy,
        config.path,
        replaceRow(rows, index, nextRow),
      ),
    );
  }

  return (
    <div
      className="flex min-w-0 flex-col gap-1"
      data-testid={`task-view-lines-${placement.id}`}
    >
      {label ? <Label>{label}</Label> : null}
      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="empty task view line list">No rows</T>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {config.columns.map((column) => (
                  <TableHead key={column.id}>
                    {placementLabel(
                      column.config.label,
                      findSchemaField(rowFields, column.config.path),
                      column.config.path,
                    )}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow key={`${placement.id}-${index}`}>
                  {config.columns.map((column) => (
                    <TableCell key={column.id} className="align-top">
                      <LineCell
                        column={column}
                        rowFields={rowFields}
                        inputRow={rowObject(inputRows[index])}
                        workingRow={rowObject(row)}
                        rowNumber={index + 1}
                        onCommitRow={(nextRow) => commitRow(index, nextRow)}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function LineCell({
  column,
  rowFields,
  inputRow,
  workingRow,
  rowNumber,
  onCommitRow,
}: {
  column: TaskTypeViewLineListPlacement['config']['columns'][number];
  rowFields: readonly TaskViewSchemaField[];
  inputRow: Record<string, unknown>;
  workingRow: Record<string, unknown>;
  rowNumber: number;
  onCommitRow: Commit;
}) {
  const header = placementLabel(
    column.config.label,
    findSchemaField(rowFields, column.config.path),
    column.config.path,
  );
  const ariaLabel = `${header} ${rowNumber}`;
  if (column.component === 'match') {
    return (
      <TaskTypeViewMatch
        placement={column}
        schemaFields={rowFields}
        input={inputRow}
        workingCopy={workingRow}
        onCommit={onCommitRow}
        hideLabel
        ariaLabel={ariaLabel}
      />
    );
  }
  return (
    <TaskTypeViewField
      placement={column}
      schemaFields={rowFields}
      input={inputRow}
      workingCopy={workingRow}
      onCommit={onCommitRow}
      hideLabel
      ariaLabel={ariaLabel}
    />
  );
}
